
class MainMenuSection
{
    public readonly rootElement: HTMLDivElement;
    public readonly headerElement: HTMLDivElement;

    private _collapsed: boolean;


    constructor(rootElement: HTMLDivElement)
    {
        const headerElement = rootElement.querySelector("[data-main-menu-header]");
        if (headerElement === null)
        {
            throw new Error("Main menu section does not contain a header element");
        }

        this.rootElement = rootElement;
        this.headerElement = headerElement as HTMLDivElement;
        this._collapsed = true;
    }

    public get collapsed(): boolean
    {
        return this._collapsed;
    }

    public set collapsed(collapsed: boolean)
    {
        if (this._collapsed !== collapsed)
        {
            if (collapsed)
            {
                this.rootElement.dataset.collapsed = "";
            }
            else
            {
                delete this.rootElement.dataset.collapsed;
            }
            this._collapsed = collapsed;
        }
    }
}

interface MainMenuEventListeners
{
    onClick: EventListener;
}


export class MainMenuService
{
    public readonly rootElement: HTMLDivElement;
    public readonly menuElement: HTMLDivElement;

    private readonly eventListeners: MainMenuEventListeners;
    private readonly sections: MainMenuSection[];

    private eventSubscription: boolean;
    private activeKey?: string;

    constructor(rootElement: HTMLDivElement)
    {
        this.rootElement = rootElement;

        this.sections = Array.from(this.rootElement.querySelectorAll("[data-main-menu-section]")).map(e => new MainMenuSection(e as HTMLDivElement));
        this.menuElement = this.rootElement.querySelector("[data-main-menu]") as HTMLDivElement;

        this.eventListeners = {
            onClick: this.handleClick.bind(this) as EventListener,
        };

        this.eventSubscription = false;

        this.subscribe();
    }

    public dispose(): void
    {
        this.unsubscribe();
    }

    private activateGroup(key: string): void
    {
        this.setVisible(key);

        this.rootElement.querySelectorAll<HTMLDivElement>("[data-main-menu-group]").forEach(e =>
        {
            if (e.dataset.mainMenuGroup === key || key === "")
            {
                e.dataset.active = "";
            }
            else
            {
                delete e.dataset.active;
            }
        });
    }

    private handleClick(event: MouseEvent): void
    {
        const target = event.target as HTMLElement;

        const activateElement = target.closest<HTMLElement>("[data-main-menu-activate]");
        const closeButton = target.closest<HTMLElement>("[close-button]");

        if (closeButton !== null)
        {
            this.setInvisible();
            return;
        }
        if (activateElement !== null)
        {
            event.stopImmediatePropagation();
            this.handleGroupKey(activateElement.dataset.mainMenuActivate as string);
            return;
        }

        const section = this.sections.find(e => e.headerElement.contains(target));
        if (section !== undefined)
        {
            event.stopImmediatePropagation();
            section.collapsed = !section.collapsed;
            return;
        }

        if (this.activeKey !== undefined && (!this.menuElement.contains(target) || target.closest("a") !== null))
        {
            this.setInvisible();
        }
    }

    private handleGroupKey(key: string): void
    {
        if (this.activeKey === key)
        {
            this.setInvisible()
        }
        else
        {
            if (this.activeKey !== undefined)
            {
                this.rootElement.dataset.hidden = "";
                this.rootElement.querySelectorAll<HTMLDivElement>("[data-main-menu-group]").forEach(e => delete e.dataset.active);
                delete this.rootElement.dataset.hidden;
                this.activateGroup(key);
            }
            else 
            {
                this.activateGroup(key);
            }
        }
    }

    private setInvisible()
    {
        document.querySelectorAll<HTMLDivElement>('[data-tab-active="true"]').forEach(e => e.setAttribute("data-tab-active", "false"));
        this.activeKey = undefined;
        delete this.rootElement.dataset.active;
        document.documentElement.style.overflow = "";
    }

    private setVisible(groupKey: string)
    {
        document.querySelectorAll<HTMLDivElement>('[data-tab-active="true"]').forEach(e => e.setAttribute("data-tab-active", "false"));
        document.querySelectorAll(`[data-main-menu-activate="${groupKey}"]`).forEach(e=> e.setAttribute("data-tab-active", "true"));
        this.activeKey = groupKey;
        this.rootElement.dataset.active = "";
        document.documentElement.style.overflow = "hidden";
    }

    private subscribe()
    {
        if (!this.eventSubscription)
        {
            document.addEventListener("click", this.eventListeners.onClick, true);
            this.eventSubscription = true;
        }
    }

    private unsubscribe()
    {
        if (this.eventSubscription)
        {
            document.removeEventListener("click", this.eventListeners.onClick, true);
            this.eventSubscription = false;
        }
    }
}